import { HttpProvider } from "../http/http.provider";
import { IAppConfig } from "../http/types/IApplicationConfig";

import { addProvider, IProviderDescriptor, ServiceEndpointConstants } from "../ServiceUtil";
import { Observable } from "rxjs";
import { share, map } from "rxjs/operators";
import { createFormData } from "../util/utilities";
import
{
    IBenchEmployee,
    IBenchEmployeeResponse,
    INewBenchEmployeeDocument,
    IBenchEmployeeDocument,
    IBenchEmployeeSubmission,
    IBenchEmployeeInterview,
    IBenchLeadResponse,
    IBenchLead,
    IBenchInterviewResponse,
    IBenchEmployeeInterviewSchedule,
    INewBenchLeadBulk,
    IBenchEmployeeAcceptedSubmission,
    IBenchEmployeeSalesRepTracking,
    IBenchEmployeeStatusTracking,
    IBenchSearchResponse
} from "./bench-employee.interface";
import { IHttpRequestConfig } from "../http";

export class BenchEmployeeDelegate
{
    constructor(private SERVICE_CONFIG: IAppConfig, private http: HttpProvider)
    {
    }

    public getAllBenchEmployees(orgId: string,
        benchType: 'raw' | 'training' | 'marketing' | 'placement' | 'declined' | 'salesRep',
        salesRepId: string)
        : Observable<IBenchEmployeeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId,
                BenchStatus: benchType,
                salesRepId: salesRepId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployees: BenchEmployeeDelegate.normalizeBenchEmployees(response.data.benchEmployees || []),
                    success: response.data.success
                };
            }),
            share());
    }

    public getBenchEmployeeById(benchEmployeeId: string)
        : Observable<IBenchEmployeeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                BenchEmployeeId: benchEmployeeId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET_BY_ID, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeId: benchEmployeeId,
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee || []),
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewBenchEmployee(newBenchEmployee: IBenchEmployee): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.CREATE, newBenchEmployee).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployees: BenchEmployeeDelegate.normalizeBenchEmployees(response.data.benchEmployees || []),
                    benchEmployeeId: response.data.benchEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public getSalesSearch(orgId: string, searchKey: string): Observable<IBenchSearchResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                orgId: orgId,
                searchKey: searchKey
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET_SEARCH, null, config).pipe(
            map((response: any) =>
            {
                return {
                    success: response.data.success,
                    benchEmployees: BenchEmployeeDelegate.normalizeBenchEmployees(response.data.saleRepSearchData.benchEmployees || []),
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.saleRepSearchData.benchLeads || [])
                };
            }),
            share());
    }

    public updateBenchEmployee(benchEmployee: IBenchEmployee): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.UPDATE, benchEmployee).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeId: benchEmployee.benchEmployeeId,
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee || []),
                    success: response.data.success
                };
            }),
            share());
    }

    public getAllBenchLeads(orgId: string)
        : Observable<IBenchLeadResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHLEAD.GET, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.benchLeads || []),
                    success: response.data.success
                };
            }),
            share());
    }

    public getBenchLeadById(benchLeadId: string)
        : Observable<IBenchLeadResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                benchLeadId: benchLeadId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHLEAD.GET_ID, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchLead: response.data.benchLead,
                    success: response.data.success,
                    benchLeadId: benchLeadId
                };
            }),
            share());
    }


    public getStageSubmissions(orgId: string)
        : Observable<IBenchEmployeeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET_STGAE_SUBMISSIONS, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeSubmission: BenchEmployeeDelegate.normalizeAcceptedBenchSubmission(response.data.benchEmployeeStageSubmission
                    ),
                    success: response.data.success
                };
            }),
            share());
    }

    public getAcceptedSubmissions(orgId: string)
        : Observable<IBenchEmployeeResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET_ONBOARD_SUBMISSION, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeSubmission: BenchEmployeeDelegate.normalizeAcceptedBenchSubmission(response.data.benchEmployeeSubmission),
                    success: response.data.success
                };
            }),
            share());
    }


    public getAllBenchInterviwes(orgId: string, salesRepId: string, fromDate: Date | string): Observable<IBenchInterviewResponse>
    {
        const config: IHttpRequestConfig = {
            params: {
                salesRepId: salesRepId,
                orgId: orgId,
                fromDate: fromDate
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.GET_INTERVIEW, null, config).pipe(
            map((response: any) =>
            {
                return {
                    benchInterviews: BenchEmployeeDelegate.normalizeInterviews(response.data.benchInterviews),
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewBenchLead(newBenchLead: IBenchLead): Observable<IBenchLeadResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHLEAD.CREATE, newBenchLead).pipe(
            map((response: any) =>
            {
                return {
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.benchLeads || []),
                    benchLeadId: response.data.benchLeadId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewBenchLeadBulk(newBenchLead: INewBenchLeadBulk): Observable<IBenchLeadResponse>
    {
        const formdata = createFormData(newBenchLead);

        return this.http.post(ServiceEndpointConstants.endpoints.BENCHLEAD.CREATE_BULK, formdata).pipe(
            map((response: any) =>
            {
                return {
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.benchLeads || []),
                    benchLeadId: response.data.benchLeadId,
                    message: response.data.message,
                    success: response.data.success
                };
            }),
            share());
    }
    public updateBenchLead(benchLead: IBenchLead): Observable<IBenchLeadResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHLEAD.UPDATE, benchLead).pipe(
            map((response: any) =>
            {
                return {
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.benchLeads || []),
                    benchLeadId: benchLead.benchLeadId,
                    success: response.data.success
                };
            }),
            share());
    }

    public transferBenchLead(benchLeadId: string): Observable<IBenchLeadResponse>
    {
        var obj = {
            benchLeadId: benchLeadId,
            clientTimeStamp: new Date().toISOString()
        };
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHLEAD.TRANSFER, obj).pipe(
            map((response: any) =>
            {
                return {
                    benchLeads: BenchEmployeeDelegate.normalizeBenchLeads(response.data.benchLeads || []),
                    benchLeadId: response.data.benchLeadId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewBenchEmployeeSubmssion(newBenchSubmission: IBenchEmployeeSubmission): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.CREATE_SUBMISSION, newBenchSubmission).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee),
                    benchEmployeeId: response.data.benchEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateBenchEmployeeSubmssion(benchSubmission: IBenchEmployeeSubmission): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.UPDATE_SUBMISSION, benchSubmission).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee),
                    benchEmployeeId: benchSubmission.benchEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createNewBenchInterview(newBenchInterview: IBenchEmployeeInterview): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.CREATE_INTERVIEW, newBenchInterview).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee),
                    benchEmployeeId: response.data.benchEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public updateBenchInterview(benchInterview: IBenchEmployeeInterview): Observable<IBenchEmployeeResponse>
    {
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.UPDATE_INTERVIEW, benchInterview).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployee: BenchEmployeeDelegate.normalizeBenchEmployee(response.data.benchEmployee),
                    benchEmployeeId: benchInterview.benchEmployeeId,
                    success: response.data.success
                };
            }),
            share());
    }

    public createBenchEmployeeResume(benchEmpDocs: INewBenchEmployeeDocument): Observable<IBenchEmployeeResponse>
    {
        const formdata = createFormData(benchEmpDocs);

        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.SAVE_RESUME, formdata).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeId: benchEmpDocs.benchEmployeeId,
                    benchEmployees: BenchEmployeeDelegate.normalizeBenchEmployees(response.data.benchEmployees),
                    success: response.data.success
                };
            }),
            share());
    }

    public onboardEmployee(benchEmployeeId: string,
                             benchEmployeeSubmissionId: string,
                             userId: string,
                             orgId: string): Observable<IBenchEmployeeResponse>
    {
        var obj = {
            benchEmployeeId: benchEmployeeId,
            benchEmployeeSubmissionId: benchEmployeeSubmissionId,
            orgId: orgId,
            userId: userId,
            clientTimeStamp: new Date().toISOString()
        };
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.ONBOARD_EMPLOYEE, obj).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeSubmission: BenchEmployeeDelegate.normalizeAcceptedBenchSubmission(response.data.benchEmployeeSubmission),
                    success: response.data.success
                };
            }),
            share());
    }

    public acceptBenchSubmission(benchEmployeeStageSubmissionId: string, userId: string, orgId: string): Observable<IBenchEmployeeResponse>
    {
        var obj = {
            benchEmployeeStageSubmissionId: benchEmployeeStageSubmissionId,
            orgId: orgId,
            userId: userId,
            clientTimeStamp: new Date().toISOString()
        };
        return this.http.post(ServiceEndpointConstants.endpoints.BENCHEMPLOYEE.ACCEPT_STAGE_SUBMISSION, obj).pipe(
            map((response: any) =>
            {
                return {
                    benchEmployeeSubmission: BenchEmployeeDelegate.normalizeAcceptedBenchSubmission(response.data.benchEmployeeStageSubmission),
                    success: response.data.success
                };
            }),
            share());
    }
    public static normalizeBenchLeads(benchLeads: any): IBenchLead[]
    {
        return benchLeads.map(benchLead =>
        {
            //benchEmployee.benchEmployeeResume = BenchEmployeeDelegate.normalizeBenchEmpDocs(benchEmployee.benchEmployeeResume || []);
            //benchEmployee.salesRepFullName = benchEmployee.salesRep ? benchEmployee.salesRep.salesRepFullName : "";
            //benchEmployee.employmentAuthorizationName = benchEmployee.employmentAuthorization ?
            //    benchEmployee.employmentAuthorization.configurationValue :
            //    "";
            //benchEmployee.accountManagerName = benchEmployee.accountManager ? benchEmployee.accountManager.managerName : "";
            //benchEmployee.benchEmployeeSubmission = BenchEmployeeDelegate.normalizeBenchSubmission(benchEmployee.benchEmployeeSubmission || []);
            //benchEmployee.interviewSchedule = BenchEmployeeDelegate.normalizeBenchInterviewSchedule(benchEmployee.interviewSchedule || []);

            return benchLead;
        });
    }

    public static normalizeBenchEmployees(benchEmployees: any): IBenchEmployee[]
    {
        return benchEmployees.map(benchEmployee =>
        {
            return BenchEmployeeDelegate.normalizeBenchEmployee(benchEmployee);
        });
    }

    private static normalizeBenchEmployee(benchEmployee: any): IBenchEmployee
    {
        benchEmployee.benchEmployeeResume = BenchEmployeeDelegate.normalizeBenchEmpDocs(benchEmployee.benchEmployeeResume || []);
        benchEmployee.salesRepFullName = benchEmployee.salesRep ? benchEmployee.salesRep.salesRepFullName : "";
        benchEmployee.employmentAuthorizationName = benchEmployee.employmentAuthorization ?
            benchEmployee.employmentAuthorization.configurationValue :
            "";
        benchEmployee.accountManagerName = benchEmployee.accountManager ? benchEmployee.accountManager.managerName : "";
        benchEmployee.benchEmployeeSubmission = BenchEmployeeDelegate.normalizeBenchSubmission(benchEmployee.benchEmployeeSubmission || []);
        
        benchEmployee.benchEmployeeStageSubmission = BenchEmployeeDelegate.normalizeBenchSubmission(benchEmployee.benchEmployeeStageSubmission || []);

        benchEmployee.interviewSchedule = BenchEmployeeDelegate.normalizeBenchInterviewSchedule(benchEmployee.interviewSchedule || []);
        benchEmployee.benchEmployeeStatusTracking = BenchEmployeeDelegate.normalizeStatusTracking(benchEmployee.benchEmployeeStatusTracking || []);
        benchEmployee.benchEmployeeSalesRepTracking = 
                                 BenchEmployeeDelegate.normalizeSalesRepTracking(benchEmployee.benchEmployeeSalesRepTracking || []);

        return benchEmployee;

    }
    private static normalizeBenchEmpDocs(docs: any): IBenchEmployeeDocument[]
    {
        return docs.map(doc =>
        {
            return {
                benchEmployeeResumeId: doc.benchEmployeeResumeId,
                benchEmployeeDocumentType: doc.benchEmployeeDocumentType,
                fileLocationPath: doc.fileLocationPath,
                fileName: doc.fileName
            };
        });
    }
    private static normalizeAcceptedBenchSubmission(benchSubmissons: any): IBenchEmployeeAcceptedSubmission[]
    {
        return benchSubmissons.map(benchSubmisson =>
        {
            return {
                benchEmployeeName: benchSubmisson.benchEmployee.fullName,
                benchSubmissionCode: benchSubmisson.benchSubmissionCode,
                benchEmployeeSubmissionId: benchSubmisson.benchEmployeeSubmissionId,
                benchEmployeeStageSubmissionId: benchSubmisson.benchEmployeeStageSubmissionId,
                benchEmployeeId: benchSubmisson.benchEmployeeId,
                salesRepId: benchSubmisson.salesRepId,
                firstVendorName: benchSubmisson.firstVendorName,
                primaryVendorName: benchSubmisson.primaryVendorName,
                firstVendorEmailAddress: benchSubmisson.firstVendorEmailAddress,
                primaryVendorEmailAddress: benchSubmisson.primaryVendorEmailAddress,
                firstVendorMobile: benchSubmisson.firstVendorMobile,
                primaryVendorMobile: benchSubmisson.primaryVendorMobile,
                clientName: benchSubmisson.clientName,
                rate: benchSubmisson.rate,
                technology: benchSubmisson.technology,
                submissionAcceptedDate: benchSubmisson.submissionAcceptedDate,
                submissionDate: benchSubmisson.submissionDate,
                salesRepFullName: benchSubmisson.salesRep ? benchSubmisson.salesRep.salesRepFullName : "",
                submissionStatus: benchSubmisson.submissionStatus
            };
        });
    }

    private static normalizeBenchSubmission(benchSubmissons: any): IBenchEmployeeSubmission[]
    {
        return benchSubmissons.map(benchSubmisson =>
        {
            return {
                benchSubmissionCode: benchSubmisson.benchSubmissionCode,
                benchEmployeeSubmissionId: benchSubmisson.benchEmployeeSubmissionId,
                benchEmployeeStageSubmissionId: benchSubmisson.benchEmployeeStageSubmissionId,

                benchEmployeeId: benchSubmisson.benchEmployeeId,
                firstVendorName: benchSubmisson.firstVendorName,
                primaryVendorName: benchSubmisson.primaryVendorName,
                firstVendorEmailAddress: benchSubmisson.firstVendorEmailAddress,
                primaryVendorEmailAddress: benchSubmisson.primaryVendorEmailAddress,
                firstVendorMobile: benchSubmisson.firstVendorMobile,
                primaryVendorMobile: benchSubmisson.primaryVendorMobile,
                clientName: benchSubmisson.clientName,
                rate: benchSubmisson.rate,
                technology: benchSubmisson.technology,
                submissionStatus: benchSubmisson.submissionStatus,
                submissionAcceptedDate: benchSubmisson.submissionAcceptedDate,
                submissionDate: benchSubmisson.submissionDate,
                salesRepFullName: benchSubmisson.salesRep ? benchSubmisson.salesRep.salesRepFullName : "",
                salesRepId: benchSubmisson.salesRepId,
                notes: benchSubmisson.notes,
                benchEmployeeInterview: BenchEmployeeDelegate.normalizeBenchInterviews(benchSubmisson.benchEmployeeInterview || [])
            };
        });
    }

    private static normalizeBenchInterviews(benchInterviews: any): IBenchEmployeeInterview[]
    {
        return benchInterviews.map(benchInterview =>
        {
            return {
                benchEmployeeInterviewId: benchInterview.benchEmployeeInterviewId,
                benchEmployeeId: benchInterview.benchEmployeeId,

                interviewType: benchInterview.interviewType,
                interviewRepId: benchInterview.interviewRepId,
                interviewStatus: benchInterview.interviewStatus,

                scheduledDateTime: new Date(benchInterview.scheduledDateTimeEpoch),
                feedBack: benchInterview.feedBack,
                scheduledDateTimeToString: benchInterview.scheduledDateTimeToString
            };
        });
    }

    private static normalizeBenchInterviewSchedule(benchInterviews: any): IBenchEmployeeInterview[]
    {
        return benchInterviews.map(benchInterview =>
        {
            return {
                ...benchInterview,

                scheduledDateTime: new Date(benchInterview.scheduledDateTimeEpoch),
                scheduledDateTimeToString: new Date(benchInterview.scheduledDateTimeEpoch).toLocaleDateString()
                    + " " + new Date(benchInterview.scheduledDateTimeEpoch).toLocaleTimeString()

            };
        });
    }

    private static normalizeSalesRepTracking(benchInterviews: any): IBenchEmployeeSalesRepTracking[]
    {
        return benchInterviews.map(benchInterview =>
        {
            return {
                ...benchInterview,
                salesRepFullName: benchInterview.salesRep ? benchInterview.salesRep.salesRepFullName : ""

            };
        });
    }
    private static normalizeStatusTracking(benchInterviews: any): IBenchEmployeeStatusTracking[]
    {
        return benchInterviews.map(benchInterview =>
        {
            return {
                ...benchInterview

            };
        });
    }
    private static normalizeInterviews(benchInterviews: any): IBenchEmployeeInterviewSchedule[]
    {
        return benchInterviews.map(benchInterview =>
        {
            return {
                benchEmployeeName: benchInterview.benchEmployeeSubmission.benchEmployee ?
                    benchInterview.benchEmployeeSubmission.benchEmployee.fullName : '',
                scheduledDateTime: new Date(benchInterview.scheduledDateTimeEpoch),
                scheduledDateTimeToString: new Date(benchInterview.scheduledDateTimeEpoch).toLocaleDateString()
                    + " " + new Date(benchInterview.scheduledDateTimeEpoch).toLocaleTimeString(),
                benchSubmissionCode: benchInterview.benchEmployeeSubmission.benchSubmissionCode,
                firstVendorName: benchInterview.benchEmployeeSubmission.firstVendorName,
                firstVendorMobile: benchInterview.benchEmployeeSubmission.firstVendorMobile,
                clientName: benchInterview.benchEmployeeSubmission.clientName,
                primaryVendorName: benchInterview.benchEmployeeSubmission.primaryVendorName,
                primaryVendorMobile: benchInterview.benchEmployeeSubmission.primaryVendorMobile,
                interviewStatus: benchInterview.interviewStatus,
                interviewType: benchInterview.interviewType,
                interviewRound: benchInterview.interviewRound,
                benchEmployeeInterviewId: benchInterview.benchEmployeeInterviewId,
                benchEmployeeSubmissionId: benchInterview.benchEmployeeSubmission.benchEmployeeSubmissionId,

                benchEmployeeId: benchInterview.benchEmployeeSubmission.benchEmployeeId
            };
        });
    }
}